import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "../../lib/admin";
import { PhotoUploader } from "./PhotoUploader";
import { Switch } from "../ui/switch";

const TEXT = [["marca", "Marca"], ["modello", "Modello"], ["colore", "Colore"], ["carburante", "Carburante"], ["cambio", "Cambio"], ["potenza", "Potenza"], ["targa", "Targa"], ["telaio", "Numero telaio"]];
const NUM = [["anno", "Anno"], ["km", "Km"]];
const SUGGEST = { carburante: ["Benzina", "Diesel", "Ibrida", "Elettrica", "GPL", "Metano"], cambio: ["Manuale", "Automatico"] };
const ALL = ["prezzo", ...TEXT.map((x) => x[0]), ...NUM.map((x) => x[0]), "descrizione"];

const inputCls = "w-full h-11 rounded-xl border border-[var(--line)] bg-white px-4 text-sm outline-none focus:border-[var(--ink)]";

export const CarForm = ({ car, onSaved, onCancel }) => {
  const [f, setF] = useState(() => Object.fromEntries(ALL.map((k) => [k, car?.[k] ?? ""])));
  const [photos, setPhotos] = useState(car?.foto ?? []);
  const [venduto, setVenduto] = useState(!!car?.venduto);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    const prezzo = parseFloat(String(f.prezzo).replace(/\./g, "").replace(",", "."));
    if (!prezzo || prezzo <= 0) return toast.error("Il prezzo è obbligatorio");
    const body = { prezzo, foto: photos, venduto };
    TEXT.forEach(([k]) => { body[k] = String(f[k]).trim() || null; });
    NUM.forEach(([k]) => { const n = parseInt(String(f[k]).replace(/\D/g, ""), 10); body[k] = Number.isFinite(n) ? n : null; });
    body.descrizione = String(f.descrizione).trim() || null;
    setSaving(true);
    try {
      if (car?.id) await adminApi.put(`/cars/${car.id}`, body);
      else await adminApi.post("/cars", body);
      toast.success("Auto salvata");
      onSaved();
    } catch (e2) { toast.error(errMsg(e2)); }
    setSaving(false);
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="car-form">
      <PhotoUploader photos={photos} setPhotos={setPhotos} />
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider">Prezzo (€) <span className="text-[var(--red)]">*</span></label>
        <input value={f.prezzo} onChange={set("prezzo")} inputMode="decimal" placeholder="es. 24900" required data-testid="car-form-prezzo" className={`${inputCls} mt-1.5 text-lg font-semibold`} />
      </div>
      <div>
        <p className="text-xs text-[var(--muted)] mb-3">Tutti i campi seguenti sono facoltativi.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {[...TEXT.slice(0, 2), ...NUM, ...TEXT.slice(2)].map(([k, l]) => (
            <div key={k}>
              <label className="text-xs font-semibold uppercase tracking-wider text-[var(--ink-2)]">{l}</label>
              <input value={f[k]} onChange={set(k)} list={SUGGEST[k] ? `list-${k}` : undefined} inputMode={NUM.some((n) => n[0] === k) ? "numeric" : undefined} data-testid={`car-form-${k}`}
                className={`${inputCls} mt-1.5 ${["targa", "telaio"].includes(k) ? "font-mono uppercase" : ""}`} />
              {SUGGEST[k] && <datalist id={`list-${k}`}>{SUGGEST[k].map((o) => <option key={o} value={o} />)}</datalist>}
            </div>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-[var(--ink-2)]">Descrizione</label>
        <textarea value={f.descrizione} onChange={set("descrizione")} rows={4} data-testid="car-form-descrizione" className="w-full mt-1.5 rounded-xl border border-[var(--line)] bg-white p-4 text-sm outline-none focus:border-[var(--ink)]" />
      </div>
      <label className="flex items-center gap-3 text-sm font-semibold">
        <Switch checked={venduto} onCheckedChange={setVenduto} data-testid="car-form-venduto" /> Segna come venduta
      </label>
      <div className="flex gap-3 pt-2 sticky bottom-0 bg-[var(--paper)] py-4 border-t border-[var(--line)]">
        <button type="button" onClick={onCancel} data-testid="car-form-cancel-btn" className="flex-1 h-12 rounded-full border border-[var(--ink)] text-sm font-semibold hover:bg-[var(--ink)] hover:text-white transition-colors">Annulla</button>
        <button disabled={saving} data-testid="car-form-save-btn" className="flex-1 h-12 rounded-full bg-[var(--red)] text-white text-sm font-semibold hover:bg-[var(--red-dark)] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salva auto
        </button>
      </div>
    </form>
  );
};
